import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { crossDomainRedirect, initDomains, isAppHost, splitMode } from "@/lib/domains";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AdminLogin from "@/pages/AdminLogin";
import Onboarding from "@/pages/Onboarding";
import PhoneGate from "@/pages/PhoneGate";
import AppLayout from "@/pages/app/AppLayout";
import Allotment from "@/pages/app/Allotment";
import ClosingRanks from "@/pages/app/ClosingRanks";
import Fees from "@/pages/app/Fees";
import Colleges from "@/pages/app/Colleges";
import Probability from "@/pages/app/Probability";
import SavedSearches from "@/pages/app/SavedSearches";
import Watchlist from "@/pages/app/Watchlist";
import Compare from "@/pages/app/Compare";
import Overview from "@/pages/app/Overview";
import Upgrade from "@/pages/app/Upgrade";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUpload from "@/pages/admin/AdminUpload";
import AdminBatches from "@/pages/admin/AdminBatches";
import AdminExport from "@/pages/admin/AdminExport";
import AdminMappings from "@/pages/admin/AdminMappings";
import AdminMasters from "@/pages/admin/AdminMasters";
import AdminReview from "@/pages/admin/AdminReview";
import AdminAliases from "@/pages/admin/AdminAliases";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminPayments from "@/pages/admin/AdminPayments";

const CONFIG_REQUEST_TIMEOUT_MS = 4_000;

const Guard = ({ children, admin = false }) => {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center text-sm text-slate-500">
        Loading NeetOne…
      </div>
    );
  if (!user) return <Navigate to={admin ? "/admin/login" : "/login"} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app/allotment" replace />;
  if (!admin && user.role !== "admin" && !user.profile_complete) return <PhoneGate />;
  if (!admin && user.role !== "admin" && user.onboarding_complete === false)
    return <Navigate to="/onboarding" replace />;
  return children;
};

const OnboardingGuard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (!user.profile_complete) return <PhoneGate />;
  if (user.onboarding_complete !== false) return <Navigate to="/app" replace />;
  return children;
};

function App() {
  const [ready, setReady] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    api
      .get("/config", { timeout: CONFIG_REQUEST_TIMEOUT_MS })
      .then(({ data }) => initDomains(data))
      .catch(() => {})
      .finally(() => {
        const target = crossDomainRedirect();
        if (target) {
          setRedirecting(true);
          window.location.replace(target);
          return;
        }
        setReady(true);
      });
  }, []);

  if (!ready || redirecting)
    return (
      <div className="flex h-screen items-center justify-center text-sm text-slate-500">
        Loading NeetOne…
      </div>
    );

  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route
            path="/"
            element={splitMode() && isAppHost() ? <Navigate to="/app" replace /> : <Landing />}
          />
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/onboarding"
            element={
              <OnboardingGuard>
                <Onboarding />
              </OnboardingGuard>
            }
          />

          <Route
            path="/app"
            element={
              <Guard>
                <AppLayout />
              </Guard>
            }
          >
            <Route index element={<Overview />} />
            <Route path="allotment" element={<Allotment />} />
            <Route path="closing-ranks" element={<ClosingRanks />} />
            <Route path="fees" element={<Fees />} />
            <Route path="colleges" element={<Colleges />} />
            <Route path="probability" element={<Probability />} />
            <Route path="saved" element={<SavedSearches />} />
            <Route path="watchlist" element={<Watchlist />} />
            <Route path="compare" element={<Compare />} />
            <Route path="upgrade" element={<Upgrade />} />
          </Route>

          <Route
            path="/admin"
            element={
              <Guard admin>
                <AdminLayout />
              </Guard>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="upload" element={<AdminUpload />} />
            <Route path="batches" element={<AdminBatches />} />
            <Route path="batches/:batchId/review" element={<AdminReview />} />
            <Route path="masters" element={<AdminMasters />} />
            <Route path="export" element={<AdminExport />} />
            <Route path="mappings" element={<AdminMappings />} />
            <Route path="aliases" element={<AdminAliases />} />
            <Route path="overview" element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="payments" element={<AdminPayments />} />
          </Route>

          <Route path="*" element={<Navigate to={splitMode() && isAppHost() ? "/app" : "/"} replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
